import React, { FC, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import type { CollapsibleSectionProps } from './types';
import { SectionContainer, SectionHeader, SectionLeft, SectionArrow, SectionTitle, SectionCount, SectionBody, ItemsList, FooterGuideLink } from './styled';

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
    title,
    count,
    isBoolean,
    defaultOpen = false,
    forceOpen,
    controlledOpen,
    tall,
    helpUrl,
    onHelpClick,
    onOpenChange,
    children
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = forceOpen || (controlledOpen !== undefined ? controlledOpen : open);
    const sectionRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        const willOpen = !open;
        setOpen(willOpen);
        onOpenChange?.(willOpen);
        if (willOpen) {
            requestAnimationFrame(() => {
                sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    return (
        <SectionContainer ref={sectionRef}>
            <SectionHeader onClick={handleToggle}>
                <SectionLeft>
                    {!forceOpen && <SectionArrow $open={isOpen}>&#9654;</SectionArrow>}
                    <SectionTitle>{title}</SectionTitle>
                </SectionLeft>
                <SectionCount $isZero={count === 0}>{isBoolean ? (count > 0 ? '✓' : '—') : count > 0 ? count : '\u2014'}</SectionCount>
            </SectionHeader>
            <SectionBody $collapsed={!isOpen}>
                {forceOpen ? (
                    children
                ) : (
                    <>
                        <ItemsList $tall={tall}>{children}</ItemsList>
                        {helpUrl && isOpen && count > 0 && (
                            <FooterGuideLink
                                href={onHelpClick ? undefined : helpUrl}
                                target={onHelpClick ? undefined : '_blank'}
                                rel={onHelpClick ? undefined : 'noopener'}
                                as={onHelpClick ? 'span' : undefined}
                                onClick={
                                    onHelpClick
                                        ? (e: React.MouseEvent) => {
                                              e.preventDefault();
                                              onHelpClick();
                                          }
                                        : undefined
                                }
                                style={onHelpClick ? { cursor: 'pointer' } : undefined}
                            >
                                {T._('Learn more', 'Admin')} &#8250;
                            </FooterGuideLink>
                        )}
                    </>
                )}
            </SectionBody>
        </SectionContainer>
    );
};
